/**
 * Stytch Session Management Hooks
 *
 * These hooks provide convenient wrappers for session management
 * operations using the Stytch B2B headless client.
 */
import { getStytchClient } from './../client/headless-client';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../core/session-durations';
/**
 * Hook to authenticate existing session
 */
export function useAuthenticateSession() {
    return async (options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.session.authenticate({
                session_duration_minutes: options?.sessionDurationMinutes || STYTCH_DEFAULT_SESSION_DURATION_MINUTES,
            });
            return {
                success: true,
                session_jwt: result.session_jwt,
                session_token: result.session_token,
                member_session: result.member_session,
                member: result.member,
                organization: result.organization,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate session',
                data: error,
            };
        }
    };
}
/**
 * Hook to exchange the current session for a session scoped to a different
 * organization (workspace switching). Mirrors useExchangeIntermediateSession's
 * shape but operates on an already-authenticated session instead of an
 * intermediate one.
 */
export function useExchangeSession() {
    return async (organizationId, sessionDurationMinutes = STYTCH_DEFAULT_SESSION_DURATION_MINUTES) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.session.exchange({
                organization_id: organizationId,
                session_duration_minutes: sessionDurationMinutes,
            });
            return {
                success: true,
                sessionJwt: result.session_jwt,
                sessionToken: result.session_token,
                memberSession: result.member_session,
                memberAuthenticated: result.member_authenticated,
                primaryRequired: result.primary_required,
                mfaRequired: result.mfa_required,
                organization: result.organization,
                member: result.member,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to exchange session',
                data: error,
            };
        }
    };
}
/**
 * Hook to revoke session (logout)
 */
export function useRevokeSession() {
    return async () => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            await stytch.session.revoke();
            return {
                success: true,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to revoke session',
                data: error,
            };
        }
    };
}
